import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {
  CancerPlanTypes,
  isLoaded as isLoadedPlans,
  UnassignedCancerPlanTypes,
} from 'ApplicationEntry/models/ProductsRiders/Cancer/CancerPlanTypes';
import {
  CancerPolicyOption,
  isLoaded as isLoadedPolicyOptions,
  UnassignedCancerPolicyOption,
} from 'ApplicationEntry/models/ProductsRiders/Cancer/PolicyOption';
import {
  isLoaded as isLoadedProducts,
  Plan,
  PlanModel,
  UnassignedPlanModel,
} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import * as I from 'immutable';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import * as RI from '../RiderInformation';

type CancerProps = {
  productType: StringUnion;
  planType: StringUnion;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  policyOption: StringUnion;
  cwa: StringUnion;
  groupTrust: boolean;
  section125: boolean;
  replacement: boolean;
  originalAge: number;
  reRate: boolean;
  policyNumber: PolicyNumber;
  policyOptions: CancerPolicyOption;
  riderInformation: RI.RiderInformation;
  productList: PlanModel;
  plantypes: CancerPlanTypes;
  overRideBillForm: boolean;
};

export class InvalidOrdinaryCancer {
  public readonly key = 'InvalidOrdinaryCancer';

  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly policyOption: StringUnion,
    public readonly cwa: StringUnion,
    public readonly groupTrust: boolean,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly originalAge: number,
    public readonly reRate: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly policyOptions: CancerPolicyOption,
    public readonly riderInformation: RI.RiderInformation,
    public readonly productList: PlanModel,
    public readonly plantypes: CancerPlanTypes,
    public readonly overRideBillForm: boolean
  ) {}
}

export class ValidOrdinaryCancer {
  public static createApp({
    productType,
    planType,
    premium,
    calculatedPremium,
    policyOption,
    cwa,
    groupTrust,
    section125,
    replacement,
    originalAge,
    reRate,
    policyNumber,
    policyOptions,
    riderInformation,
    productList,
    plantypes,
    overRideBillForm,
  }: CancerProps) {
    return this.create(
      productType,
      planType,
      premium,
      calculatedPremium,
      policyOption,
      cwa,
      groupTrust,
      section125,
      replacement,
      originalAge,
      reRate,
      policyNumber,
      policyOptions,
      riderInformation,
      productList,
      plantypes,
      overRideBillForm
    );
  }

  public static create(
    productType: StringUnion,
    planType: StringUnion,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    policyOption: StringUnion,
    cwa: StringUnion,
    groupTrust: boolean,
    section125: boolean,
    replacement: boolean,
    originalAge: number,
    reRate: boolean,
    policyNumber: PolicyNumber,
    policyOptions: CancerPolicyOption,
    riderInformation: RI.RiderInformation,
    productList: PlanModel,
    plantypes: CancerPlanTypes,
    overRideBillForm: boolean
  ) {
    return isNonEmptyString(productType) &&
      planType &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      groupTrust &&
      section125 &&
      replacement &&
      originalAge &&
      reRate &&
      policyNumber &&
      RI.isLoaded(riderInformation) &&
      riderInformation.Data.codes.size > 0 &&
      riderInformation.Data.amounts.size > 0 &&
      isLoadedProducts(productList) &&
      isLoadedPlans(plantypes) &&
      isLoadedPolicyOptions(policyOptions)
      ? new ValidOrdinaryCancer(
          productType,
          planType,
          premium,
          calculatedPremium,
          policyOption,
          cwa,
          groupTrust,
          section125,
          replacement,
          originalAge,
          reRate,
          policyNumber,
          policyOptions,
          riderInformation,
          productList,
          plantypes,
          overRideBillForm
        )
      : new InvalidOrdinaryCancer(
          productType,
          planType,
          premium,
          calculatedPremium,
          policyOption,
          cwa,
          groupTrust,
          section125,
          replacement,
          originalAge,
          reRate,
          policyNumber,
          policyOptions,
          riderInformation,
          productList,
          plantypes,
          overRideBillForm
        );
  }

  public readonly key = 'ValidOrdinaryCancer';
  constructor(
    public readonly productType: StringUnion,
    public readonly planType: StringUnion,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly policyOption: StringUnion,
    public readonly cwa: StringUnion,
    public readonly groupTrust: boolean,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly originalAge: number,
    public readonly reRate: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly policyOptions: CancerPolicyOption,
    public readonly riderInformation: Loaded<RI.default>,
    public readonly productList: Loaded<Plan[]>,
    public readonly plantypes: Loaded<string[]>,
    public readonly overRideBillForm: boolean
  ) {}
}

export type OrdinaryCancer = InvalidOrdinaryCancer | ValidOrdinaryCancer;

export type OrdinaryCancerOptions<TReturn> = ToPartialMatchOptions<OrdinaryCancer, 'key', TReturn>;

export const matchOrdinaryCancer = <TReturn>(options: OrdinaryCancerOptions<TReturn>) => (
  app: OrdinaryCancer
): TReturn => {
  switch (app.key) {
    case 'InvalidOrdinaryCancer':
      return options.InvalidOrdinaryCancer ? options.InvalidOrdinaryCancer(app) : options.default(app);
    case 'ValidOrdinaryCancer':
      return options.ValidOrdinaryCancer ? options.ValidOrdinaryCancer(app) : options.default(app);
  }
};

export const EmptyOrdinaryCancer = new InvalidOrdinaryCancer(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  false,
  0,
  false,
  EmptyPolicyNumber,
  new UnassignedCancerPolicyOption(),
  new RI.UnassignedRiderInformation(),
  new UnassignedPlanModel(),
  new UnassignedCancerPlanTypes(),
  false
);

export const changeProductType = (productType: string) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({
    ...app,
    productType: NonEmptyString.create(productType),
    calculatedPremium: new EmptyString(),
    premium: new EmptyString(),
    cwa: new EmptyString(),
    policyNumber: EmptyPolicyNumber,
    reRate: false,
    originalAge: 0,
  });

export const changePlanType = (planType: string) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({
    ...app,
    planType: NonEmptyString.create(planType),
    calculatedPremium: new EmptyString(),
    premium: new EmptyString(),
    cwa: new EmptyString(),
    policyNumber: EmptyPolicyNumber,
    reRate: false,
    originalAge: 0,
  });

export const changePolicyOption = (policyOption: string) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, policyOption: NonEmptyString.create(policyOption)});

export const changePremium = (premium: string) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, premium: NonEmptyString.create(premium)});

export const changeCalculatedPremium = (calculatedPremium: string) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeGroupTrust = (groupTrust: boolean) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, groupTrust});

export const changeSection125 = (section125: boolean) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({
    ...app,
    replacement,
    originalAge: 0,
    policyNumber: app.replacement && !app.reRate ? ValidPolicyNumber.create(new EmptyString()) : app.policyNumber,
  });

export const changeOriginalAge = (originalAge: number) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, originalAge});

export const changeReRate = (reRate: boolean) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({
    ...app,
    reRate,
    policyNumber: app.reRate && !app.replacement ? ValidPolicyNumber.create(new EmptyString()) : app.policyNumber,
  });

export const changePolicyNumber = (policyNumber: StringUnion) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changeProductList = (products: PlanModel) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, productList: products});

export const changePlanTypes = (plans: CancerPlanTypes) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, plantypes: plans});

export const changePolicyOptions = (policyOptionList: CancerPolicyOption) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, policyOptions: policyOptionList});

export const changeOverrideBillForm = (overRideBillForm: boolean) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({...app, overRideBillForm});

export const removeRider = (index: number) => (app: OrdinaryCancer) => {
  const existingListAmounts: I.List<number> = RI.isLoaded(app.riderInformation)
    ? app.riderInformation.Data.amounts
    : I.List<number>();
  const newListAmounts = existingListAmounts.delete(index);
  const existingListCodes: I.List<string> = RI.isLoaded(app.riderInformation)
    ? app.riderInformation.Data.codes
    : I.List<string>();
  const newListCodes = existingListCodes.delete(index);

  const riderCodes: RI.RiderCode[] = RI.isLoaded(app.riderInformation)
    ? app.riderInformation.Data.riderData
    : ([] as RI.RiderCode[]);

  return ValidOrdinaryCancer.createApp({
    ...app,
    riderInformation: new Loaded(new RI.default(newListCodes, newListAmounts, riderCodes)),
  });
};

export const changeRiderInformationData = (riderAmount: number, riderCode: string, index: number) => (
  app: OrdinaryCancer
) => {
  const riderInformation = RI.default.changeRiderInformation(
    riderAmount,
    riderCode,
    index,
    RI.isLoaded(app.riderInformation) ? app.riderInformation.Data.amounts : I.List<number>(),
    RI.isLoaded(app.riderInformation) ? app.riderInformation.Data.codes : I.List<string>()
  );

  const riderCodes: RI.RiderCode[] = RI.isLoaded(app.riderInformation)
    ? app.riderInformation.Data.riderData
    : ([] as RI.RiderCode[]);

  return ValidOrdinaryCancer.createApp({
    ...app,
    riderInformation: new Loaded(
      new RI.default(riderInformation.riderCodes, riderInformation.riderAmounts, riderCodes)
    ),
  });
};

export const changeRiderInformation = (riderInformation: RI.RiderInformation) => (app: OrdinaryCancer) =>
  ValidOrdinaryCancer.createApp({
    ...app,
    riderInformation,
  });
